import { useCallback, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation } from '@apollo/client'
import { observer } from 'mobx-react'

import { ButtonGroup, Card, CardBody, CardHeader } from 'Components/_theme'
import { DataSelectInputAsync } from 'Components/DataSelectInputAsync/DataSelectInputAsync'
import { DataSelectInputAsyncOption } from 'Components/DataSelectInputAsync/DataSelectInputAsync.types'

import { useActiveUser } from 'Hooks'

import { getFullContactName } from 'Utilities'

import { E2E_REASSIGN_EMPLOYEE_SUBMIT } from 'Constants/e2e'
import {
  rentalReassign,
  rentalReassignVariables,
} from 'Constants/graphql/mutations/__generated__/rentalReassign'
import { REASSIGN_RENTAL } from 'Constants/graphql/mutations/rentalReassign'
import {
  getEmployeeByName,
  getEmployeeByNameVariables,
} from 'Constants/graphql/queries/employee/__generated__/getEmployeeByName'
import { GET_EMPLOYEE_BY_NAME } from 'Constants/graphql/queries/employee/getEmployeeByName'

import { iconReassignDevice } from 'Icons/iconReassignDevice'

import ApolloLoader from 'Portal/src/ApolloLoader'

import {
  ReassignEmployeeModalProps,
  ReassignEmployeeModalSelectedOption,
} from './ReassignEmployeeModal.types'

export const ReassignEmployeeModal = observer(
  (props: ReassignEmployeeModalProps) => {
    const { onClose, showModal, rental, fetchData } = props
    const intl = useIntl()
    const { activeOrganisation } = useActiveUser()
    const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false)
    const [selectedEmployee, setSelectedEmployee] =
      useState<ReassignEmployeeModalSelectedOption | null>(null)

    const [
      postReassignRentalMutation,
      {
        loading,
        error: reassignRentalMutationError,
        reset: resetReassignRentalMutation,
      },
    ] = useMutation<rentalReassign, rentalReassignVariables>(REASSIGN_RENTAL, {
      onCompleted: () => {
        fetchData()
        setIsSubmissionSuccess(true)
      },
      variables: {
        employeeID: selectedEmployee?.id || '',
        rentalID: rental.id,
      },
    })

    useEffect(() => {
      resetReassignRentalMutation()
    }, [showModal, onClose, resetReassignRentalMutation])

    const handleReassignRentalSubmission = useCallback(
      () => postReassignRentalMutation(),
      [postReassignRentalMutation],
    )

    const handleReassignRentalSubmissionSuccessClose = useCallback(() => {
      setIsSubmissionSuccess(false)

      onClose()
    }, [onClose])

    const handleFetchSelectionOptions = useCallback(
      async (searchTerm: string) => {
        if (!activeOrganisation?.id) {
          throw new Error('No active organisation id provided')
        }

        const response = await ApolloLoader.apolloClient?.query<
          getEmployeeByName,
          getEmployeeByNameVariables
        >({
          query: GET_EMPLOYEE_BY_NAME,
          variables: {
            orgId: activeOrganisation.id.toString(),
            searchTerm,
          },
        })

        const mappedResponse =
          response?.data.organisation.employees?.employees?.map<DataSelectInputAsyncOption>(
            employee => ({
              displayName: `${getFullContactName(employee.contact)} ${
                employee.contact.email ? ` (${employee.contact.email})` : ''
              }`,
              name: getFullContactName(employee.contact),
              value: employee.id,
            }),
          ) || []

        return Promise.resolve(mappedResponse)
      },
      [activeOrganisation?.id],
    )

    const handleOptionSelection = useCallback(
      (item: DataSelectInputAsyncOption) =>
        setSelectedEmployee({ id: item.value, name: item.name }),
      [setSelectedEmployee],
    )

    const handleRenderNoResultsLabel = useCallback(
      () => intl.formatMessage({ id: 'ReassignEmployee_NoResults' }),
      [intl],
    )

    const handleRenderNoResultsInvalidLabel = useCallback(
      (limit: number) =>
        intl.formatMessage(
          { id: 'ReassignEmployee_NoResultsInvalid' },
          { limit },
        ),
      [intl],
    )

    return (
      <Card>
        <CardHeader icon={iconReassignDevice}>
          <FormattedMessage id="ReassignEmployee_ModalTitle" />
        </CardHeader>

        <CardBody>
          <Form>
            {isSubmissionSuccess ? (
              <p className="mt-0 mb-0">
                <FormattedMessage
                  id="ReassignEmployee_SubmissionSuccess"
                  values={{
                    employeeName: selectedEmployee?.name || '',
                  }}
                />
              </p>
            ) : (
              <DataSelectInputAsync
                errorText={intl.formatMessage({
                  id: 'ReassignEmployee_SubmissionError',
                })}
                fetchSelectOptions={handleFetchSelectionOptions}
                hasError={!!reassignRentalMutationError}
                labelTranslationId="ReassignEmployee_ModalSearchEmployee"
                onOptionSelectionHandler={handleOptionSelection}
                renderNoResults={handleRenderNoResultsLabel}
                renderNoResultsInvalid={handleRenderNoResultsInvalidLabel}
                sourceId="reassignEmployeeSelect"
              />
            )}
          </Form>

          {!isSubmissionSuccess ? (
            <ButtonGroup
              leftButton={{
                onClick: onClose,
                text: intl.formatMessage({ id: 'Cancel' }),
              }}
              rightButton={{
                'data-e2e': E2E_REASSIGN_EMPLOYEE_SUBMIT,
                disabled: !selectedEmployee || loading,
                onClick: handleReassignRentalSubmission,
                text: intl.formatMessage({ id: 'Assign' }),
              }}
            />
          ) : (
            <ButtonGroup
              rightButton={{
                onClick: handleReassignRentalSubmissionSuccessClose,
                text: intl.formatMessage({ id: 'Close' }),
              }}
            />
          )}
        </CardBody>
      </Card>
    )
  },
)
